/**
 * @module builder
 */

import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { UpdateableAreaSubstrate, QuoteItemSection } from '../index'
import type { EditableRate } from '../../options'
import { createRate, getFeature } from '../../options'
import type { UpdateableProduct } from './index'

import { moveTo } from '../util/move-to/move-to'
import { uuid } from '../../index'

/**
 * Create a new UpdateableAreaSubstrate from the given args to be edited/updated
 */
export function createAreaSubstrate(
  args: {
    rate?: EditableRate
    products?: UpdateableProduct[]
    quoteSection?: QuoteItemSection
    quote?: QuoteDocument
    options?: OptionsDocument
  } = {}
): UpdateableAreaSubstrate {
  const { rate, products = [], options } = args
  const useDefaultRateVals = options
    ? getFeature({ options, path: 'quotes.areaSubstrates.advancedOptions' })?.defaultDimensions ?? false
    : false

  const section = rate ? rate.rateSection : ''
  const name = rate ? rate.label : ''
  const clientLabel = rate ? rate.clientLabel : ''

  const coats = rate && rate.defaultApplications ? rate.defaultApplications : 2
  const rateValue = rate ? rate.values[coats - 1] : 0
  const prep = rate?.prep ?? 0

  const quoteSection = args.quoteSection ? args.quoteSection : 'bid'

  const length =
    typeof rate?.defaultLength === 'number' && useDefaultRateVals
      ? { useCustom: true, custom: rate.defaultLength, default: '' }
      : { useCustom: false, custom: '', default: '' }

  const height =
    typeof rate?.defaultHeight === 'number' && useDefaultRateVals
      ? { useCustom: true, custom: rate.defaultHeight, default: '' }
      : { useCustom: false, custom: '', default: '' }

  const width =
    typeof rate?.defaultWidth === 'number' && useDefaultRateVals
      ? { useCustom: true, custom: rate.defaultWidth, default: '' }
      : { useCustom: false, custom: '', default: '' }

  const r = {
    key: uuid(),
    type: 'areaSubstrate' as const,
    section,
    name,
    clientLabel,
    prep,
    hours: {
      useCustom: false,
      default: 0,
      custom: 0
    },
    price: {
      useCustom: false,
      default: 0,
      custom: 0
    },
    rate: {
      ...(rate ? rate : createRate())
    },
    products,
    quantity: 1,
    coats,
    length,
    width,
    height,
    rateValue: {
      useCustom: false,
      default: rateValue,
      custom: 0
    },
    quoteSection,

    notes: rate && rate.notes,

    workOrderOnly: rate && rate.workOrderOnly ? rate.workOrderOnly : false,
    showPrice: rate && rate.showPrice ? rate.showPrice : false,
    hideQuantity: rate && rate.hideQuantity ? rate.hideQuantity : false,
    hideQuantityOnWorkOrder: rate && rate.hideQuantityOnWorkOrder ? rate.hideQuantityOnWorkOrder : false,
    showProduct: rate && rate.showProduct ? rate.showProduct : false,
    showCoats: rate && rate.showCoats ? rate.showCoats : false,
    showPrep: rate && rate.showPrep ? rate.showPrep : false,
    showDimensions: rate && rate.showDimensions ? rate.showDimensions : false,
    excludeDiscount: rate && rate.excludeDiscount ? rate.excludeDiscount : false,
    excludeTax: rate && rate.excludeTax ? rate.excludeTax : false,

    hourlyRate: rate && rate.hourlyRate >= 0 ? rate.hourlyRate : null,
    overrideHourlyRate: rate && rate.overrideHourlyRate ? rate.overrideHourlyRate : false,
    useInches: rate && rate.useInches ? rate.useInches : false
  }

  return moveTo(r, quoteSection)
}
